import { useEffect, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useRouterState } from "@tanstack/react-router";
import { ArrowUpCircle, AtSign, Building2, Eye, Inbox, LayoutDashboard, LogOut, Mail, MapPin, Monitor, Moon, PanelLeftClose, PanelLeftOpen, Plus, ScrollText, Share2, Sparkles, Store, Sun, X } from "lucide-react";
import { api, type Me } from "../lib/api";
import { capabilities } from "../lib/access";
import { useThemePref, type ThemePref } from "../lib/theme";
import { cn } from "../lib/utils";
import { Button } from "../components/ui/button";
import { Dialog, Menu, MenuContent, MenuItem, MenuLabel, MenuSeparator, MenuTrigger } from "../components/ui/overlay";
import { Tip } from "../components/ui/primitives";
import { LocationPicker, pickerVia } from "./SelectLocations";
import { leaveViewAs, useViewAsEnded, VIEW_AS_ENDED_TEXT, ViewAsBanner, ViewAsPicker } from "./ViewAs";
import { AskUsageDialog } from "../features/account/AskUsageDialog";
import { SidebarNav } from "./SidebarNav";

const COLLAPSED_KEY = "sideNavCollapsed";

/** Whether the side nav is collapsed to an icon rail, remembered per browser. */
function useSideNavCollapsed() {
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(COLLAPSED_KEY) === "1";
    } catch {
      return false;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSED_KEY, collapsed ? "1" : "0");
    } catch {
      /* private mode */
    }
  }, [collapsed]);
  return [collapsed, setCollapsed] as const;
}

export function Shell({ me, children }: { me: Me; children: ReactNode }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const path = useRouterState({ select: (s) => s.location.pathname });
  const dashboards = useQuery({ queryKey: ["dashboards"], queryFn: api.dashboards });
  const workspaces = useQuery({ queryKey: ["workspaces"], queryFn: api.workspaces, staleTime: Infinity });
  // Only staff are given any, and only worth showing when there's somewhere else to go.
  const otherWorkspaces = (workspaces.data ?? []).filter((w) => w.host !== window.location.hostname);
  const [theme, setTheme] = useThemePref();
  const can = capabilities(me);
  // While viewing as someone, their locations are theirs: no changing them.
  const via = can.viewAs ? null : pickerVia(me);
  const [picking, setPicking] = useState(false);
  const [choosingViewAs, setChoosingViewAs] = useState(false);
  const [askUsageOpen, setAskUsageOpen] = useState(false);
  const [viewAsEnded, dismissViewAsEnded] = useViewAsEnded();
  const [collapsed, setCollapsed] = useSideNavCollapsed();

  const create = useMutation({
    mutationFn: () => api.createDashboard("Untitled dashboard"),
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ["dashboards"] });
      navigate({ to: "/d/$id", params: { id: d.dashboard.id } });
    },
  });
  const logout = useMutation({ mutationFn: api.logout, onSuccess: () => window.location.assign("/?signin=signed_out") });

  // Only shares the person accepted are listed (in a folder or under Shared with me); the rest wait on /shared.
  const accepted = dashboards.data?.some((d) => d.shareStatus === "accepted") ?? false;
  const waiting = dashboards.data?.filter((d) => d.shareStatus === "pending").length ?? 0;
  const setAside = dashboards.data?.some((d) => d.shareStatus === "dismissed") ?? false;
  const ThemeIcon = theme === "dark" ? Moon : theme === "light" ? Sun : Monitor;

  const itemClass = (active: boolean) =>
    cn(
      "relative flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm transition-colors",
      collapsed && "justify-center",
      active ? "bg-surface-2 font-medium text-foreground" : "text-muted hover:bg-surface-2 hover:text-foreground",
    );

  const NavItem = ({ id, title, icon: Icon, update }: { id: string; title: string; icon: typeof LayoutDashboard; update?: number }) => (
    <Tip content={collapsed ? (update !== undefined ? `${title} · update available: v${update}` : title) : undefined} side="right">
      <Link to="/d/$id" params={{ id }} className={itemClass(path === `/d/${id}`)} aria-label={collapsed ? title : undefined}>
        <Icon className="size-4 shrink-0 opacity-70" />
        {!collapsed && <span className="truncate">{title}</span>}
        {update !== undefined &&
          (collapsed ? (
            <span className="absolute right-1 top-1 size-1.5 rounded-full bg-warn-fg" />
          ) : (
            <Tip content={`Update available: v${update}`} side="right">
              <ArrowUpCircle className="ml-auto size-3.5 shrink-0 text-warn-fg" aria-label={`Update available: v${update}`} />
            </Tip>
          ))}
      </Link>
    </Tip>
  );

  return (
    <div className="flex h-full">
      <aside className={cn("flex shrink-0 flex-col border-r border-border bg-surface transition-[width] duration-200", collapsed ? "w-14" : "w-60")}>
        <div className={cn("flex items-center gap-2 py-4", collapsed ? "flex-col px-2" : "px-4")}>
          <div className="flex size-7 shrink-0 items-center justify-center rounded-lg bg-primary">
            <svg viewBox="0 0 32 32" className="size-4"><rect x="5" y="15" width="5" height="12" rx="2" fill="#3987e5" /><rect x="13.5" y="9" width="5" height="18" rx="2" fill="var(--primary-foreground)" /><rect x="22" y="4" width="5" height="23" rx="2" fill="#199e70" /></svg>
          </div>
          {!collapsed && <span className="flex-1 truncate text-sm font-semibold tracking-tight">Dashboards</span>}
          <Tip content={collapsed ? "Expand sidebar" : "Collapse sidebar"} side="right">
            <button
              onClick={() => setCollapsed((c) => !c)}
              className="rounded-md p-1 text-subtle hover:bg-surface-2 hover:text-foreground"
              aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
              aria-expanded={!collapsed}
            >
              {collapsed ? <PanelLeftOpen className="size-4" /> : <PanelLeftClose className="size-4" />}
            </button>
          </Tip>
        </div>
        <nav className={cn("flex-1 overflow-y-auto overflow-x-hidden px-2", collapsed ? "space-y-2" : "space-y-4")}>
          {/* The app's own pages, grouped tight; the dashboard lists below get the wider gap. */}
          <div className="space-y-0.5">
            <Tip content={collapsed ? "Marketplace" : undefined} side="right">
              <Link to="/marketplace" className={itemClass(path.startsWith("/marketplace"))} aria-label={collapsed ? "Marketplace" : undefined}>
                <Store className="size-4 shrink-0 opacity-70" />
                {!collapsed && <span>Marketplace</span>}
              </Link>
            </Tip>
            {/* Staff only (who may schedule reports), and never while viewing as someone (viewAsTier is unset then). */}
            {me.viewAsTier && (
              <Tip content={collapsed ? "My schedules" : undefined} side="right">
                <Link to="/schedules" className={itemClass(path.startsWith("/schedules"))} aria-label={collapsed ? "My schedules" : undefined}>
                  <Mail className="size-4 shrink-0 opacity-70" />
                  {!collapsed && <span>My schedules</span>}
                </Link>
              </Tip>
            )}
            {me.viewAsTier && (
              <Tip content={collapsed ? "Contacts" : undefined} side="right">
                <Link to="/contacts" className={itemClass(path.startsWith("/contacts"))} aria-label={collapsed ? "Contacts" : undefined}>
                  <AtSign className="size-4 shrink-0 opacity-70" />
                  {!collapsed && <span>Contacts</span>}
                </Link>
              </Tip>
            )}
            {/* Super users only, likewise. */}
            {me.viewAsTier === "super" && (
              <Tip content={collapsed ? "Audit log" : undefined} side="right">
                <Link to="/admin/audit" className={itemClass(path.startsWith("/admin/audit"))} aria-label={collapsed ? "Audit log" : undefined}>
                  <ScrollText className="size-4 shrink-0 opacity-70" />
                  {!collapsed && <span>Audit log</span>}
                </Link>
              </Tip>
            )}
            {me.viewAsTier === "super" && (
              <Tip content={collapsed ? "Ask access" : undefined} side="right">
                <Link to="/admin/ask" className={itemClass(path.startsWith("/admin/ask"))} aria-label={collapsed ? "Ask access" : undefined}>
                  <Sparkles className="size-4 shrink-0 opacity-70" />
                  {!collapsed && <span>Ask access</span>}
                </Link>
              </Tip>
            )}
          </div>
          <SidebarNav
            dashboards={dashboards.data}
            collapsed={collapsed}
            disabled={!can.write}
            showShared={accepted || waiting > 0 || setAside}
            itemClass={itemClass}
            renderItem={(d) => {
              // Folders hold shares too; the icon says which ones those are.
              if (d.sharedBy) return <NavItem id={d.id} title={d.title} icon={Share2} />;
              const f = d.installedFrom;
              const update = f?.latestVersion !== undefined && f.latestVersion > f.version ? f.latestVersion : undefined;
              return <NavItem id={d.id} title={d.title} icon={LayoutDashboard} update={update} />;
            }}
            mineHeader={(newFolder) =>
              !can.write ? (
                !collapsed && <div className="px-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-subtle">Theirs</div>
              ) : collapsed ? (
                <div className="mb-2 border-t border-border pt-2">
                  <Tip content="New dashboard" side="right">
                    <button onClick={() => create.mutate()} className={cn(itemClass(false), "w-full")} aria-label="New dashboard">
                      <Plus className="size-4" />
                    </button>
                  </Tip>
                </div>
              ) : (
                <div className="flex items-center justify-between px-2 pb-1">
                  <span className="text-[11px] font-semibold uppercase tracking-wide text-subtle">{can.viewAs ? "Theirs" : "Mine"}</span>
                  <div className="flex items-center gap-0.5">
                    {newFolder}
                    <Tip content="New dashboard">
                      <button onClick={() => create.mutate()} className="rounded-md p-0.5 text-subtle hover:bg-surface-2 hover:text-foreground" aria-label="New dashboard">
                        <Plus className="size-3.5" />
                      </button>
                    </Tip>
                  </div>
                </div>
              )
            }
            sharedHeader={
              collapsed ? (
                <div className="mb-2 border-t border-border pt-2">
                  <Tip content={waiting ? `${waiting} new shared dashboard${waiting === 1 ? "" : "s"}` : "Shared dashboards"} side="right">
                    <Link to="/shared" className={itemClass(path === "/shared")} aria-label="Shared dashboards">
                      <Inbox className="size-4" />
                      {waiting > 0 && <span className="absolute right-1 top-1 size-1.5 rounded-full bg-accent" />}
                    </Link>
                  </Tip>
                </div>
              ) : (
                <div className="flex items-center justify-between px-2 pb-1">
                  <span className="text-[11px] font-semibold uppercase tracking-wide text-subtle">{can.viewAs ? "Shared with them" : "Shared with me"}</span>
                  <Link
                    to="/shared"
                    className={cn(
                      "rounded-md px-1.5 py-0.5 text-[11px] hover:bg-surface-2",
                      waiting ? "bg-accent-soft font-medium text-accent" : "text-subtle hover:text-foreground",
                    )}
                  >
                    {waiting ? `${waiting} new` : "All"}
                  </Link>
                </div>
              )
            }
          />
        </nav>
        <div className="border-t border-border p-2">
          <Menu>
            <MenuTrigger
              className={cn("flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left hover:bg-surface-2", collapsed && "justify-center px-0")}
              aria-label={collapsed ? (me.name ?? me.userId) : undefined}
            >
              <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-accent-soft text-xs font-semibold text-accent">
                {(me.name ?? me.userId).slice(0, 1).toUpperCase()}
              </span>
              {!collapsed && (
                <>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium">{me.name ?? me.userId}</span>
                    <span className="block truncate text-[11px] text-subtle">
                      {can.viewAs ? `viewed by ${can.viewAs.by.name}` : me.roles.join(", ")} · {me.locationIds.length} location{me.locationIds.length === 1 ? "" : "s"}
                    </span>
                  </span>
                  <ThemeIcon className="size-3.5 text-subtle" />
                </>
              )}
            </MenuTrigger>
            <MenuContent align="start">
              <MenuLabel>Theme</MenuLabel>
              {(["system", "light", "dark"] as ThemePref[]).map((t) => (
                <MenuItem key={t} onSelect={() => setTheme(t)}>
                  {t === "system" ? <Monitor className="size-4" /> : t === "light" ? <Sun className="size-4" /> : <Moon className="size-4" />}
                  <span className="capitalize">{t}</span>
                  {theme === t && <span className="ml-auto text-accent">•</span>}
                </MenuItem>
              ))}
              <MenuSeparator />
              {otherWorkspaces.length > 0 && (
                <>
                  <MenuLabel>Workspace</MenuLabel>
                  {otherWorkspaces.map((w) => (
                    <MenuItem key={w.id} onSelect={() => window.location.assign(`https://${w.host}/`)}>
                      <Building2 className="size-4" /> {w.name}
                    </MenuItem>
                  ))}
                  <MenuSeparator />
                </>
              )}
              {via && (
                <MenuItem onSelect={() => setPicking(true)}>
                  <MapPin className="size-4" /> Change locations
                </MenuItem>
              )}
              {me.canAsk && (
                <MenuItem onSelect={() => setAskUsageOpen(true)}>
                  <Sparkles className="size-4" /> Ask usage
                </MenuItem>
              )}
              {me.viewAsTier && (
                <MenuItem onSelect={() => setChoosingViewAs(true)}>
                  <Eye className="size-4" /> View as someone…
                </MenuItem>
              )}
              {can.viewAs && (
                <MenuItem onSelect={() => void api.exitViewAs().finally(() => leaveViewAs("exited"))}>
                  <LogOut className="size-4" /> Exit view as {me.name ?? me.userId}
                </MenuItem>
              )}
              <MenuItem onSelect={() => logout.mutate()}><LogOut className="size-4" /> Sign out</MenuItem>
            </MenuContent>
          </Menu>
        </div>
      </aside>
      <main className="min-w-0 flex-1 overflow-y-auto">
        {can.viewAs && <ViewAsBanner me={me} viewAs={can.viewAs} />}
        {viewAsEnded && !can.viewAs && (
          <div role="status" className="flex items-center gap-3 border-b border-border bg-accent-soft px-6 py-2.5 text-sm">
            <Eye className="size-4 shrink-0 text-accent" />
            <span className="flex-1">{VIEW_AS_ENDED_TEXT[viewAsEnded]}</span>
            <button onClick={dismissViewAsEnded} aria-label="Dismiss" className="rounded p-0.5 hover:bg-accent/10"><X className="size-3.5" /></button>
          </div>
        )}
        {via && !me.locationIds.length && (
          <div className="flex items-center gap-3 border-b border-border bg-accent-soft px-6 py-2.5 text-sm">
            <MapPin className="size-4 shrink-0 text-accent" />
            <span className="flex-1">Choose the locations you want to see. Dashboards stay empty until you do.</span>
            <Button variant="primary" onClick={() => setPicking(true)}>Choose locations</Button>
          </div>
        )}
        {children}
      </main>
      {me.canAsk && <AskUsageDialog open={askUsageOpen} onOpenChange={setAskUsageOpen} />}
      {me.viewAsTier && (
        <Dialog open={choosingViewAs} onOpenChange={setChoosingViewAs} title="View as someone" className="w-full max-w-lg p-8">
          <ViewAsPicker tier={me.viewAsTier} />
        </Dialog>
      )}
      {via && (
        <Dialog open={picking} onOpenChange={setPicking} title="Choose locations" className="w-full max-w-lg p-8">
          <PickerForSession via={via} onDone={() => setPicking(false)} />
        </Dialog>
      )}
    </div>
  );
}

/** The picker, starting from the session's current locations. */
function PickerForSession({ via, onDone }: { via: NonNullable<ReturnType<typeof pickerVia>>; onDone: () => void }) {
  const qc = useQueryClient();
  const current = useQuery({ queryKey: ["locations"], queryFn: api.locations });
  // Wait for a fresh list: the picker starts from it once.
  if (current.isFetching) return null;
  return (
    <LocationPicker
      via={via}
      initial={(current.data ?? []).map((l) => ({ ...l, place: null }))}
      onDone={() => {
        // The session now has different locations: everything shown was for the old ones.
        qc.invalidateQueries();
        onDone();
      }}
    />
  );
}
